<template>
  <AppShell>
    <!-- Back / breadcrumb -->
    <div class="flex items-center gap-3 text-sm text-ink-muted mb-5">
      <button class="inline-flex items-center gap-2 hover:text-white transition-colors" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <span class="text-ink-faint">/</span>
      <button class="hover:text-white transition-colors" @click="goBack">Machine Labs</button>
      <i class="fas fa-chevron-right text-[10px] text-ink-faint"></i>
      <span class="text-white font-medium truncate">{{ lab.title }}</span>
    </div>

    <!-- Lab header card -->
    <div class="card mb-6 p-6 relative overflow-hidden">
      <div class="absolute inset-0 bg-hero-glow opacity-40"></div>
      <div class="relative">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="badge bg-info/15 text-info"><i class="fas fa-server"></i> Machine Lab</span>
          <span v-if="lab.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i> Subscriber Only</span>
          <span v-if="lab.isCompleted" class="badge bg-success text-black"><i class="fas fa-check"></i> Completed</span>
        </div>
        <h1 class="text-2xl sm:text-3xl font-extrabold text-white mb-4">{{ lab.title }}</h1>
        <div class="rounded-xl bg-surface-light border border-line py-4 px-5 text-left prose prose-invert max-w-none
          prose-headings:text-white prose-a:text-brand hover:prose-a:text-brand-light
          prose-img:rounded-lg prose-img:shadow-md prose-pre:bg-base-800 prose-pre:border prose-pre:border-line
          prose-pre:text-slate-50 prose-img:mx-auto prose-img:my-4 prose-code:text-brand-light">
          <vue-markdown :source="lab.description" :options="{ html: false, breaks: true, linkify: true }" :plugins="plugins" />
        </div>
      </div>
    </div>

    <!-- Lab Environment and VPN Section -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      <!-- Lab Environment (2/3 width) -->
      <div class="lg:col-span-2 card p-6">
        <h2 class="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <i class="fas fa-desktop text-brand"></i> Lab Environment
        </h2>

        <!-- Machine Info Card - When Running -->
        <div v-if="instanceInfo && machineStatus === 'running'" class="rounded-xl border border-success/30 bg-success/[0.06] p-5 mb-4">
          <div class="flex items-center justify-between mb-4">
            <h3 class="font-semibold text-white flex items-center gap-2">
              <i class="fas fa-server text-success"></i> Machine Instance
            </h3>
            <span class="inline-flex items-center gap-2 text-xs font-semibold text-success">
              <span class="relative flex h-2.5 w-2.5">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-60"></span>
                <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
              </span>
              Online
            </span>
          </div>
          <div class="text-sm">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <span class="text-ink-faint text-xs uppercase tracking-wider">Machine</span>
                <div class="font-bold text-white mt-0.5">{{ instanceInfo.MachineLab?.title || 'Container' }}</div>
              </div>
              <div>
                <span class="text-ink-faint text-xs uppercase tracking-wider">Target IP</span>
                <div class="flex items-center gap-2 mt-0.5">
                  <code class="font-mono font-bold text-success bg-base-800 border border-line rounded px-2 py-0.5">{{ instanceInfo.containerInfo?.ip || 'Configuring...' }}</code>
                  <button
                    v-if="instanceInfo.containerInfo?.ip"
                    @click="copyIp(instanceInfo.containerInfo.ip)"
                    class="text-ink-faint hover:text-brand transition-colors"
                    :title="copiedIp ? 'Copied!' : 'Copy IP'"
                  >
                    <i :class="copiedIp ? 'fas fa-check text-success' : 'fas fa-copy'"></i>
                  </button>
                </div>
              </div>
              <div>
                <span class="text-ink-faint text-xs uppercase tracking-wider">Status</span>
                <div :class="getStatusClass(instanceInfo.containerInfo?.status || instanceInfo.status)" class="font-bold capitalize mt-0.5">
                  {{ (instanceInfo.containerInfo?.status || instanceInfo.status || '').charAt(0).toUpperCase() + (instanceInfo.containerInfo?.status || instanceInfo.status || '').slice(1) }}
                </div>
              </div>
            </div>
            <!-- Multi-service details -->
            <div v-if="instanceInfo.containerInfo?.services?.length > 1" class="mt-3 pt-3 border-t border-line">
              <span class="text-ink-faint text-xs uppercase tracking-wider mb-2 block">Services</span>
              <div class="grid gap-2">
                <div v-for="svc in instanceInfo.containerInfo.services" :key="svc.service_name"
                  class="flex items-center justify-between bg-base-800 border border-line rounded-lg px-3 py-2">
                  <div class="flex items-center gap-3">
                    <i class="fas fa-server text-ink-faint text-xs"></i>
                    <span class="font-mono text-ink text-xs">{{ svc.service_name }}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <code v-if="svc.hostname" class="text-xs text-brand font-mono">{{ svc.hostname }}</code>
                    <code class="text-xs text-success font-mono font-bold">{{ svc.vpn_ip }}</code>
                    <button @click="copyIp(svc.vpn_ip)" class="text-ink-faint hover:text-brand transition-colors text-xs">
                      <i class="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- /etc/hosts setup hint when any service has a hostname -->
            <div v-if="hasHostnames" class="mt-3 pt-3 border-t border-line">
              <div class="rounded-lg border border-info/30 bg-info/[0.06] p-3 text-xs text-ink-muted">
                <div class="flex items-start gap-2">
                  <i class="fas fa-circle-info text-info mt-0.5"></i>
                  <div class="flex-1 space-y-2">
                    <p>
                      To use the hostnames above, add them to your local
                      <code class="text-brand-light">/etc/hosts</code>
                      (or <code class="text-brand-light">C:\Windows\System32\drivers\etc\hosts</code> on Windows).
                      The VPN does not push DNS &mdash; you map each hostname to its IP yourself.
                    </p>
                    <pre class="bg-base-800 border border-line rounded p-2 overflow-x-auto text-[11px] text-slate-50"><code>{{ hostsFileSnippet }}</code></pre>
                    <p class="text-ink-faint">
                      Without this, only the raw IP addresses will work.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Machine Status Card - Only when stopped or starting -->
        <div v-if="machineStatus === 'stopped' || machineStatus === 'starting'" class="rounded-xl border border-dashed border-line bg-surface-light/40 mb-6">
          <!-- Placeholder content when stopped -->
          <div v-if="machineStatus === 'stopped'" class="text-center py-12">
            <div class="text-ink-faint mb-4">
              <i class="fas fa-server text-4xl mb-3"></i>
              <p class="text-lg mb-2 text-ink-muted">No machine instance running</p>
              <p class="text-sm">Start a machine to begin working on this lab</p>
            </div>
          </div>

          <!-- Starting content -->
          <div v-if="machineStatus === 'starting'" class="text-center py-12">
            <div class="text-yellow-400 mb-4">
              <i class="fas fa-cog fa-spin text-4xl mb-3"></i>
              <p class="text-lg mb-2">Machine is starting...</p>
              <p class="text-sm text-ink-muted">This may take a few minutes, please wait</p>
            </div>
          </div>
        </div>

        <!-- Countdown and Controls -->
        <div v-if="(machineStatus === 'running' || isExtending) && (scheduledShutdownAt || instanceInfo?.scheduledShutdownAt)" class="rounded-xl border border-yellow-600/40 bg-yellow-500/10 p-4 mb-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 text-sm">
              <i class="fas fa-clock text-yellow-400"></i>
              <span class="text-yellow-300">Auto-shutdown in: </span>
              <span class="text-yellow-100 font-bold">{{ formatShutdownTime(scheduledShutdownAt || instanceInfo?.scheduledShutdownAt) }}</span>
            </div>
            <div class="text-xs text-yellow-400">
              Extensions: {{ instanceInfo?.extensionCount || 0 }}/{{ instanceInfo?.maxExtensions || 3 }}
            </div>
          </div>
        </div>

        <!-- Machine Controls -->
        <div class="flex items-center justify-between">
          <div v-if="lab.isExclusive && !usageStats.isSubscribed" class="text-yellow-400 text-sm flex items-center gap-2">
            <i class="fas fa-crown"></i>
            <span>Subscription required to start machines</span>
          </div>
          <div class="flex space-x-3 ml-auto">
            <button v-if="machineStatus === 'stopped'" @click="startMachine"
              :disabled="isLoading || (lab.isExclusive && !usageStats.isSubscribed)"
              class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none">
              <i class="fas fa-play"></i> Start Machine
            </button>
            <button v-if="machineStatus === 'running'" @click="stopMachine" :disabled="isLoading"
              class="btn bg-brand-gradient text-white hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed">
              <i class="fas fa-stop"></i> Stop Machine
            </button>
            <button v-if="machineStatus === 'running'" @click="extendTime"
              :disabled="isLoading || isExtending || (instanceInfo && instanceInfo.extensionCount >= instanceInfo.maxExtensions)"
              class="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed">
              <i class="fas fa-clock"></i>
              {{ isExtending ? 'Extending...' : 'Extend (+1h)' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Sidebar with Usage Stats and VPN -->
      <div class="space-y-6">
        <!-- Usage Statistics -->
        <div class="card p-5">
          <h3 class="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <i class="fas fa-chart-bar text-info"></i> Usage Statistics
          </h3>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-ink-muted">Weekly:</span>
              <span class="font-bold text-white">{{ usageStats.weeklyUsage }}/{{ usageStats.weeklyLimit }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-ink-muted">Remaining:</span>
              <span class="text-success font-bold">{{ usageStats.remainingUsage }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-ink-muted">Plan:</span>
              <span :class="usageStats.isSubscribed ? 'text-success' : 'text-brand'" class="font-bold">
                {{ usageStats.isSubscribed ? 'Premium' : 'Free' }}
              </span>
            </div>
          </div>
        </div>

        <!-- VPN Section -->
        <div class="card p-5">
          <h3 class="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <i class="fas fa-shield-halved text-brand"></i> VPN Access
          </h3>

          <!-- VPN Management Buttons -->
          <div class="space-y-2 mb-4">
            <button @click="downloadVPN" class="btn-primary w-full">
              <i class="fas fa-download"></i> Download VPN
            </button>
            <button @click="revokeVPN" class="btn-secondary w-full">
              <i class="fas fa-sync"></i> Regenerate
            </button>
          </div>

          <!-- Compact VPN Instructions -->
          <div class="rounded-lg border border-line bg-surface-light p-3 text-xs text-ink-muted space-y-1.5">
            <p><span class="text-brand font-bold">1.</span> Download VPN config</p>
            <p><span class="text-brand font-bold">2.</span> Install OpenVPN client</p>
            <p><span class="text-brand font-bold">3.</span> Import .ovpn file</p>
            <p><span class="text-brand font-bold">4.</span> Connect to access machine</p>
          </div>

          <!-- Troubleshooting hint -->
          <div class="mt-3 rounded-lg border border-yellow-600/30 bg-yellow-500/[0.06] p-3 text-xs text-ink-muted">
            <div class="flex items-start gap-2">
              <i class="fas fa-triangle-exclamation text-yellow-400 mt-0.5"></i>
              <p>
                Having trouble connecting? Try
                <button @click="revokeVPN" class="text-brand hover:text-brand-light underline font-medium">regenerating</button>
                the VPN profile and re-importing it. If the issue persists, contact admin.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Flag Submission -->
    <div class="card p-6">
      <!-- Lab Completed State -->
      <div v-if="lab.isCompleted" class="text-center py-8">
        <div class="text-success mb-6">
          <i class="fas fa-check-circle text-6xl mb-4"></i>
          <h2 class="text-2xl font-bold mb-2 text-white">Machine Lab Completed!</h2>
          <p class="text-success text-lg">Congratulations! You have successfully completed this machine lab.</p>
          <p class="text-ink-muted text-sm mt-2">Flag submitted successfully!</p>
        </div>
      </div>

      <!-- Flag Submission Form -->
      <div v-else>
        <h2 class="text-lg font-bold text-white mb-4">Submit Flag</h2>

        <!-- Subscription Required Warning -->
        <div v-if="lab.isExclusive && !usageStats.isSubscribed" class="rounded-xl border border-yellow-600/40 bg-yellow-500/10 p-4 mb-4">
          <div class="flex items-center gap-2">
            <i class="fas fa-crown text-yellow-400"></i>
            <span class="text-yellow-300 text-sm">This is an exclusive lab. Subscription required to submit flags.</span>
          </div>
        </div>

        <div class="flex space-x-4">
          <input v-model="submittedFlag" type="text" placeholder="Enter the flag you found..."
            :disabled="lab.isExclusive && !usageStats.isSubscribed"
            class="input flex-1 disabled:opacity-50 disabled:cursor-not-allowed" />
          <button @click="submitFlag"
            :disabled="!submittedFlag.trim() || (lab.isExclusive && !usageStats.isSubscribed)"
            class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none">
            Submit Flag
          </button>
        </div>
      </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import markdownItLatex from "markdown-it-latex";
import markdownItHighlight from "markdown-it-highlightjs";
import VueMarkdown from 'vue-markdown-render';
import 'highlight.js/styles/github-dark.css';
import 'markdown-it-latex/dist/index.css';

export default {
    name: "MachineLabPage",
    components: { AppShell, VueMarkdown },
    data() {
        return {
            lab: {
                id: "",
                title: "",
                description: "",
                isExclusive: false,
                isCompleted: false,
                flag: ""
            },
            machineStatus: "stopped", // stopped, starting, running
            submittedFlag: "",
            isLoading: false,
            instanceInfo: null,
            usageStats: {
                weeklyUsage: 0,
                weeklyLimit: 15,
                remainingUsage: 15,
                isSubscribed: false,
                activeInstance: null
            },
            scheduledShutdownAt: null,
            isExtending: false,
            copiedIp: false,
            plugins: [markdownItLatex, markdownItHighlight]
        };
    },
    computed: {
        labId() {
            return this.$route.params.id;
        },
        hasHostnames() {
            const svcs = this.instanceInfo?.containerInfo?.services || [];
            return svcs.some(s => s.hostname);
        },
        hostsFileSnippet() {
            const svcs = this.instanceInfo?.containerInfo?.services || [];
            return svcs
                .filter(s => s.hostname && s.vpn_ip)
                .map(s => `${s.vpn_ip}\t${s.hostname}`)
                .join("\n");
        },
    },
    methods: {
        async fetchLab() {
            try {
                const response = await apiClient.get(`/machine-labs/${this.labId}`);
                this.lab = response.data;
            } catch (error) {
                console.error("Error fetching machine lab:", error);
                // Use dummy data based on the provided structure
                this.lab = {
                    id: this.labId,
                    title: "JS Obfuscation",
                    description: "**apa wae wes**\n\nThis is a machine lab for JavaScript obfuscation techniques. Learn how to analyze and deobfuscate JavaScript code to find hidden flags.\n\n- Analyze obfuscated code\n- Use debugging tools\n- Find hidden patterns",
                    isExclusive: true,
                    isCompleted: false,
                    flag: "FLAG{j5_0bfu5c4t10n_m4st3r}"
                };
            }
        },
        async startMachine() {
            this.isLoading = true;
            this.machineStatus = "starting";
            try {
                toastr.info("Starting machine lab... This may take a few minutes. Please wait.");

                const response = await apiClient.post('/machine-labs/start', {
                    machineLabId: this.labId
                });

                this.instanceInfo = response.data.instance;
                this.scheduledShutdownAt = response.data.scheduledShutdownAt;
                this.machineStatus = "running";

                toastr.success(response.data.message || "Machine started successfully!");

                // Fetch updated usage stats
                await this.fetchUsageStats();
            } catch (error) {
                this.machineStatus = "stopped";
                if (error.response?.status === 403) {
                    toastr.error(error.response.data.message || "Access denied. Subscription required.");
                } else if (error.response?.status === 429) {
                    toastr.error("Usage limit reached. Please upgrade your subscription or wait for reset.");
                } else {
                    toastr.error(error.response?.data?.message || "Failed to start machine");
                }
            } finally {
                this.isLoading = false;
            }
        },
        async stopMachine() {
            if (!this.instanceInfo?.id) return;

            this.isLoading = true;
            try {
                await apiClient.post(`/machine-labs/instances/${this.instanceInfo.id}/stop`);
                this.machineStatus = "stopped";
                this.instanceInfo = null;
                this.scheduledShutdownAt = null;
                toastr.info("Machine stopped successfully");

                // Fetch updated usage stats
                await this.fetchUsageStats();
            } catch (error) {
                toastr.error(error.response?.data?.message || "Failed to stop machine");
            } finally {
                this.isLoading = false;
            }
        },
        async extendTime() {
            if (!this.instanceInfo?.id) return;

            this.isExtending = true;
            try {
                const response = await apiClient.post(`/machine-labs/instances/${this.instanceInfo.id}/extend`, {
                    extensionMinutes: 60 // Extend by 1 hour
                });

                // Update the scheduled shutdown time and extension count
                this.scheduledShutdownAt = response.data.scheduledShutdownAt;
                if (this.instanceInfo) {
                    this.instanceInfo.extensionCount = (this.instanceInfo.extensionCount || 0) + 1;
                    this.instanceInfo.scheduledShutdownAt = response.data.scheduledShutdownAt;
                }

                toastr.success("Machine time extended by 1 hour!");
            } catch (error) {
                if (error.response?.status === 403) {
                    toastr.error("Cannot extend: Subscription required for time extensions");
                } else if (error.response?.status === 400) {
                    toastr.error(error.response.data.message || "Extension limit reached");
                } else {
                    toastr.error(error.response?.data?.message || "Failed to extend machine time");
                }
            } finally {
                this.isExtending = false;
            }
        },
        async fetchInstanceInfo() {
            if (!this.instanceInfo?.id) return;

            try {
                const response = await apiClient.get(`/machine-labs/instances/${this.instanceInfo.id}/info`);
                this.instanceInfo = { ...this.instanceInfo, ...response.data };
            } catch (error) {
                console.error("Error fetching instance info:", error);
            }
        },
        async fetchUsageStats() {
            try {
                const response = await apiClient.get('/machine-labs/usage');
                this.usageStats = response.data;

                // Update machine status based on active instance
                if (response.data.activeInstance) {
                    // Check if the active instance is for this specific lab
                    if (response.data.activeInstance.machineLabId === this.labId) {
                        this.instanceInfo = response.data.activeInstance;
                        this.machineStatus = response.data.activeInstance.status || 'running';
                        this.scheduledShutdownAt = response.data.activeInstance.scheduledShutdownAt;
                    }
                } else {
                    // No active instance, reset to stopped
                    this.machineStatus = "stopped";
                    this.instanceInfo = null;
                    this.scheduledShutdownAt = null;
                }
            } catch (error) {
                console.error("Error fetching usage stats:", error);
                // Use dummy data based on your provided structure
                this.usageStats = {
                    weeklyUsage: 1,
                    weeklyLimit: 15,
                    remainingUsage: 14,
                    isSubscribed: false,
                    activeInstance: {
                        id: "ffec1605-8fc5-4ac3-b02d-442625cd3d00",
                        containerId: "1fd69d10-d68d-44d8-b2b9-93d2fda9b38f",
                        userId: "2d71e96d-f2a7-4d75-98f4-867385f1694c",
                        machineLabId: "c218426c-2cef-4b47-aae8-0d5c4eff0d91",
                        status: "starting",
                        startedAt: "2025-09-30T04:35:06.784Z",
                        scheduledShutdownAt: "2025-09-30T06:35:06.784Z",
                        extensionCount: 0,
                        maxExtensions: 3,
                        createdAt: "2025-09-30T04:35:06.785Z",
                        updatedAt: "2025-09-30T04:35:06.785Z",
                        MachineLab: {
                            id: "c218426c-2cef-4b47-aae8-0d5c4eff0d91",
                            title: "JS Obfuscation",
                            description: "apa wae wes"
                        }
                    }
                };

                // Apply the same logic for dummy data
                if (this.usageStats.activeInstance && this.usageStats.activeInstance.machineLabId === this.labId) {
                    this.instanceInfo = this.usageStats.activeInstance;
                    this.machineStatus = this.usageStats.activeInstance.status || 'running';
                    this.scheduledShutdownAt = this.usageStats.activeInstance.scheduledShutdownAt;
                }
            }
        },
        async submitFlag() {
            try {
                const response = await apiClient.post('/machine-labs/submit-flag', {
                    machineLabId: this.labId,
                    flag: this.submittedFlag
                });

                this.lab.isCompleted = true;
                toastr.success(response.data.message || "Correct flag! Lab completed successfully!");

                // Update usage stats
                await this.fetchUsageStats();
            } catch (error) {
                if (error.response?.status === 400) {
                    toastr.error(error.response.data.message || "Incorrect flag. Try again!");
                } else {
                    toastr.error(error.response?.data?.message || "Error submitting flag");
                }
            }
        },
        async downloadVPN() {
            try {
                toastr.info("Preparing VPN configuration download...");

                const response = await apiClient.get('/machine-labs/vpn/download', {
                    responseType: 'blob'
                });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'machine-lab-vpn.ovpn');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                toastr.success("VPN configuration downloaded! Import it into your OpenVPN client.");
            } catch (error) {
                toastr.error(error.response?.data?.message || "Failed to download VPN configuration");
            }
        },
        async revokeVPN() {
            try {
                await apiClient.post('/machine-labs/vpn/revoke');
                toastr.success("VPN profile revoked and regenerated. Download the new configuration.");
            } catch (error) {
                toastr.error(error.response?.data?.message || "Failed to revoke VPN profile");
            }
        },
        openWebConsole() {
            toastr.info("Web console feature is coming soon!");
        },
        formatDate(dateString) {
            if (!dateString) return '';
            return new Date(dateString).toLocaleString();
        },
        formatShutdownTime(dateString) {
            if (!dateString) return '';
            const shutdownTime = new Date(dateString);
            const now = new Date();
            const diffMs = shutdownTime - now;
            const diffMins = Math.floor(diffMs / (1000 * 60));

            if (diffMins <= 0) return 'Expired';
            if (diffMins < 60) return `${diffMins}m`;

            const hours = Math.floor(diffMins / 60);
            const minutes = diffMins % 60;
            return `${hours}h ${minutes}m`;
        },
        getStatusClass(status) {
            const classes = {
                'running': 'text-success',
                'starting': 'text-yellow-400',
                'stopped': 'text-brand'
            };
            return classes[status] || 'text-ink-muted';
        },
        async copyIp(ip) {
            try {
                await navigator.clipboard.writeText(ip);
                this.copiedIp = true;
                setTimeout(() => { this.copiedIp = false; }, 1500);
            } catch (e) {
                toastr.info(ip);
            }
        },
        goBack() {
            this.$router.push('/machine-labs');
        }
    },
    async mounted() {
        await this.fetchLab();
        await this.fetchUsageStats();

        // Set up polling for instance info if machine is running or starting
        if ((this.machineStatus === 'running' || this.machineStatus === 'starting') && this.instanceInfo) {
            this.pollingInterval = setInterval(() => {
                this.fetchUsageStats();
            }, 10000); // Poll every 10 seconds
        }
    },
    beforeUnmount() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
    },
};
</script>

<style scoped>
.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
